import * as React from 'react';

import { translate } from '~l10n/client';
import { isAfter } from 'date-fns';

import { ViewPricingTableEventAdapter } from '~analytics/adapters/ViewPricingTableEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { Link } from '~shared/components/Link';
import {
  LegacyNoReport,
  LegacyNotFoundReport,
  NoReport,
} from '~teacher/components/reports/empty';
import { useReportsCutOffDate } from '~teacher/components/reports/useReportCutOffDate';

import Constants from '../../../../../shared/Constants';
import platform from '../../../../../shared/Platform';
import CloseX from '../../../../shared/components/buttons/CloseX';
import IconLabelButton from '../../../../shared/components/buttons/IconLabelButton';
import MoreButton from '../../../../shared/components/buttons/MoreButton';
import Anchor from '../../../../shared/components/elements/Anchor';
import Input from '../../../../shared/components/elements/Input';
import Select from '../../../../shared/components/elements/Select';
import Pagination from '../../../../shared/components/Pagination';
import Loader from '../../../../shared/components/status/Loader';
import ArchiveIcon from '../../../../shared/icons/ArchiveIcon';
import CheckIcon from '../../../../shared/icons/CheckIcon';
import InfoIcon from '../../../../shared/icons/InfoIcon';
import RestoreIcon from '../../../../shared/icons/RestoreIcon';
import SearchIcon from '../../../../shared/icons/SearchIcon';
import SortDownIcon from '../../../../shared/icons/SortDownIcon';
import SortUpIcon from '../../../../shared/icons/SortUpIcon';
import TrashIcon from '../../../../shared/icons/TrashIcon';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const utils = require('../../../../../shared/Utils');

const EndOfReports = {
  End: () => {
    const reportsCutOffDateQuery = useReportsCutOffDate();
    if (reportsCutOffDateQuery.isLoading) return null;
    if (reportsCutOffDateQuery.data.days === Infinity) return null;

    return (
      <tr>
        <td
          className="text-center text-san text-base"
          style={{ padding: 0 }}
          colSpan={100}
        >
          <span>
            {translate(
              'Upgrade your plan to access reports older than {0} days.'
            ).format(reportsCutOffDateQuery.data?.days)}
          </span>{' '}
          <Link
            className="font-600 text-cyan-700 hocus:text-cyan-600"
            includeCommonStyles
            href="#pricing"
            onClick={() =>
              new ViewPricingTableEventAdapter({
                source: 'paywall',
                paywall_feature: PaywallScreens.ReportListLimitEnd,
              }).sendAnalytics()
            }
          >
            {translate('Upgrade now')}
          </Link>
        </td>
      </tr>
    );
  },
  Start: () => {
    const reportsCutOffDateQuery = useReportsCutOffDate();
    if (reportsCutOffDateQuery.isLoading) return null;
    if (reportsCutOffDateQuery.data.days === Infinity) return null;

    return (
      <tr>
        <td
          className="text-center text-san text-base"
          style={{ padding: 0 }}
          colSpan={100}
        >
          <span>
            {translate('End of {0}-day report history.').format(
              reportsCutOffDateQuery.data?.days
            )}
          </span>{' '}
          <Link
            className="font-600 text-cyan-700 hocus:text-cyan-600"
            includeCommonStyles
            href="#pricing"
            onClick={() =>
              new ViewPricingTableEventAdapter({
                source: 'paywall',
                paywall_feature: PaywallScreens.ReportListLimitStart,
              }).sendAnalytics()
            }
          >
            {translate('Upgrade now')}
          </Link>
        </td>
      </tr>
    );
  },
};

export interface ReportsController {
  pageClicked: () => void;
  allChanged: () => void;
  deleteClicked: () => void;
  purgeClicked: (reportId?: number) => void;
  reportToggled: (reportId: number) => void;
  clearSearchClicked: () => void;
  previousClicked: () => void;
  columnClicked: (columnName: string) => void;
  restoreClicked: (reportId?: number) => void;
  searchClicked: () => void;
  stateChanged: (value: string) => void;
  searchTextChanged: (searchText: string) => void;
  unarchiveClicked: () => void;
  nextClicked: () => void;
  archiveClicked: () => void;
}

export interface ReportsModel {
  bannerText: string;
  state: string;
  lastSearchText: string;
  waitingText: string;
  allChecked: boolean;
  showBanner: boolean;
  hasSearched: boolean;
  bannerSubText: string;
  totalResults: number;
  sortColumn: string;
  searchText: string;
  sortDirection: 'asc' | 'desc';
  fetchingReports: boolean;
  page: number;
  roomNames: any[];
}

interface Props {
  controller: ReportsController;
  model: ReportsModel;

  reports: any[];
  anySelected: boolean;
}
export default function ReportsView({
  controller,
  model,
  reports,
  anySelected,
}: Props) {
  let reportsChildren = [];

  const reportsCutOffDate = useReportsCutOffDate();

  const {
    bannerText,
    state,
    lastSearchText,
    waitingText,
    allChecked,
    showBanner,
    hasSearched,
    bannerSubText,
    totalResults,
    sortColumn,
    searchText,
    sortDirection,
    fetchingReports,
    page,
  } = model;

  const {
    pageClicked,
    allChanged,
    deleteClicked,
    purgeClicked,
    reportToggled,
    clearSearchClicked,
    previousClicked,
    columnClicked,
    restoreClicked,
    searchClicked,
    stateChanged,
    searchTextChanged,
    unarchiveClicked,
    nextClicked,
    archiveClicked,
  } = controller;

  if (fetchingReports || reportsCutOffDate.isLoading) {
    reportsChildren = [
      <Loader
        key="reportsChildren-Loader-1"
        className="reports-loader"
        text={waitingText}
      />,
    ];
  } else if (reports.length === 0) {
    // If there are no reports in the archived section, or the trash show the old message.
    if (lastSearchText.length > 0) {
      console.log('legacy not found report');
      reportsChildren = [
        <LegacyNotFoundReport
          key="not-found-1"
          lastSearchText={lastSearchText}
        />,
      ];
    } else if (
      state === 'archived' ||
      state === 'trashed' ||
      state === 'deleted_rooms'
    ) {
      reportsChildren = [<LegacyNoReport key="not-found-1" />];
    } else {
      reportsChildren = [<NoReport key="empty-report-1" />];
    }
  }

  const stateChangeButtons = [];

  if (state === 'archived') {
    stateChangeButtons.push(
      <IconLabelButton
        key="stateChangeButtons-IconLabelButton-1"
        className="state-change-button"
        disabled={!anySelected}
        icon={ArchiveIcon}
        label={translate('Unarchive', true)}
        onClick={unarchiveClicked}
      />,
      <IconLabelButton
        className="state-change-button"
        disabled={!anySelected}
        icon={TrashIcon}
        label={translate('Delete', true)}
        onClick={deleteClicked}
      />
    );
  } else if (state === 'trashed') {
    stateChangeButtons.push(
      <IconLabelButton
        key="stateChangeButtons-IconLabelButton-2"
        className="state-change-button"
        disabled={!anySelected}
        icon={RestoreIcon}
        label={translate('Restore', true)}
        // Zero arguments means restore all checked reports.
        onClick={() => restoreClicked()}
      />,
      <IconLabelButton
        key="stateChangeButtons-IconLabelButton-3"
        className="state-change-button"
        disabled={!anySelected}
        icon={TrashIcon}
        label={translate('Delete Forever', true)}
        // Zero arguments means purge all checked reports.
        onClick={() => purgeClicked()}
      />
    );
    stateChangeButtons[platform.isPhone() ? 'unshift' : 'push'](
      <div key="stateChangeButtons-div-1" className="auto-delete-container">
        <InfoIcon className="auto-delete-icon" color="#309bff" />
        <span className="auto-delete-text">
          {translate(
            'Reports in the trash will automatically be deleted after 30 days.'
          )}
        </span>
      </div>
    );
  } else {
    stateChangeButtons.push(
      <IconLabelButton
        key="stateChangeButtons-IconLabelButton-3"
        className="state-change-button"
        disabled={!anySelected}
        icon={ArchiveIcon}
        label={translate('Archive', true)}
        onClick={archiveClicked}
      />,
      <IconLabelButton
        key="stateChangeButtons-IconLabelButton-4"
        className="state-change-button"
        disabled={!anySelected}
        icon={TrashIcon}
        label={translate('Delete', true)}
        onClick={deleteClicked}
      />
    );
  }

  if (reports.length > 0) {
    const SortIcon = sortDirection === 'asc' ? SortUpIcon : SortDownIcon;

    const headerChildren = [
      <th key="headerChildren-th-1" className="reports-all-column">
        <div id="reports-all-container">
          <input
            className="vertical-align-middle"
            type="checkbox"
            id="reports-all-check"
            checked={allChecked}
            onChange={allChanged}
          />
          <label
            className="vertical-align-middle reports-all-label"
            htmlFor="reports-all-check"
          >
            {translate('All', true)}
          </label>
        </div>
      </th>,
      <th
        key="headerChildren-th-2"
        className={
          state === 'trashed'
            ? 'reports-name-trash-column'
            : 'reports-name-column'
        }
      >
        <span
          className={
            'reports-sort-header' +
            (sortColumn === 'name' ? ' reports-active-sort-header' : '')
          }
          title={translate('Sort by name')}
          onClick={() => columnClicked('name')}
        >
          <span className="reports-column-header-text">
            {translate('Name', true)}
          </span>
          <SortIcon
            className={
              'reports-sort-icon' +
              (sortColumn === 'name' ? ' reports-active-sort-icon' : '')
            }
          />
        </span>
      </th>,
      <th
        key="headerChildren-th-3"
        className={
          state === 'trashed'
            ? 'reports-date-trash-column'
            : 'reports-date-column'
        }
      >
        <span
          className={
            'reports-sort-header' +
            (sortColumn === 'date' ? ' reports-active-sort-header' : '')
          }
          title={translate('Sort by date')}
          onClick={() => columnClicked('date')}
        >
          <span className="reports-column-header-text">
            {translate('Date', true)}
          </span>
          <SortIcon
            className={
              'reports-sort-icon' +
              (sortColumn === 'date' ? ' reports-active-sort-icon' : '')
            }
          />
        </span>
      </th>,
      <th key="headerChildren-th-4" className="reports-room-column">
        <span
          className={
            'reports-sort-header' +
            (sortColumn === 'room' ? ' reports-active-sort-header' : '')
          }
          title={translate('Sort by room')}
          onClick={() => columnClicked('room')}
        >
          <span className="reports-column-header-text">
            {translate('Room', true)}
          </span>
          <SortIcon
            className={
              'reports-sort-icon' +
              (sortColumn === 'room' ? ' reports-active-sort-icon' : '')
            }
          />
        </span>
      </th>,
      <th key="headerChildren-th-5" className="reports-type-column">
        <span
          className={
            'reports-sort-header' +
            (sortColumn === 'type' ? ' reports-active-sort-header' : '')
          }
          title={translate('Sort by type')}
          onClick={() => columnClicked('type')}
        >
          <span className="reports-column-header-text">
            {translate('Type', true)}
          </span>
          <SortIcon
            className={
              'reports-sort-icon' +
              (sortColumn === 'type' ? ' reports-active-sort-icon' : '')
            }
          />
        </span>
      </th>,
    ];

    if (state === 'trashed') {
      if (platform.isMobile()) {
        headerChildren.push(
          <th key="headerChildren-th-6" className="reports-more-column">
            {''}
          </th>
        );
      } else {
        headerChildren.push(
          <th key="headerChildren-th-7" className="reports-restore-column">
            {translate('Restore', true)}
          </th>,
          <th key="headerChildren-th-8" className="reports-purge-column">
            {translate('Delete Forever', true)}
          </th>
        );
      }
    }

    let tableChildren = [<tr key="tableChildren-tr-1">{headerChildren}</tr>];

    if (sortColumn === 'date' && sortDirection === 'asc') {
      tableChildren = [
        ...tableChildren,
        <EndOfReports.Start key="end-of-reports-start" />,
      ];
    }

    const filteredReports = reportsCutOffDate.isLoading
      ? []
      : reports.filter(({ endTime: reportEndTime }) => {
          const { cutOff } = reportsCutOffDate.data;
          return isAfter(reportEndTime, cutOff);
        });

    for (const report of filteredReports) {
      const nameChildren = [];

      nameChildren.push(
        <Anchor
          key="nameChildren-Anchor-1"
          href={`#results/${report.activityId}`}
          keyValue={`report-${report.activityId}`}
          label={report.name}
          tooltipText={translate('View Report')}
        />
      );

      const nameSubChildren = [];
      const dateText = new Intl.DateTimeFormat('en-US').format(report.endTime);

      if (state === 'trashed') {
        if (platform.isMobile()) {
          nameSubChildren.push(
            <span key="nameSubChildren-span-1" className="date-sub-text">
              {dateText + (report.daysRemaining ? ' | ' : '')}
            </span>
          );
        }

        nameSubChildren.push(
          <span
            key="nameSubChildren-span-2"
            className={
              report.daysRemaining > 3
                ? 'days-remaining-blue'
                : 'days-remaining-red'
            }
          >
            {report.daysRemaining +
              ' ' +
              (report.daysRemaining > 1 ? translate('days') : translate('day'))}
          </span>
        );
      } else {
        nameSubChildren.push(
          <span key="nameSubChildren-span-3" className="standard-sub-text">
            {report.standard}
          </span>
        );
      }

      if (nameSubChildren.length > 0) {
        nameChildren.push(
          <div key="nameChildren-div-1" className="name-sub-text">
            {nameSubChildren}
          </div>
        );
      }

      const dateChildren = [
        <span key="dateChildren-span-1" className="reports-date-text">
          {dateText}
        </span>,
        <span key="dateChildren-span-2" className="reports-time-text">
          {new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
          }).format(report.endTime)}
        </span>,
      ];

      const cellChildren = [
        <td key="cellChildren-td-1" className="reports-checkbox">
          <input
            className="vertical-align-middle"
            type="checkbox"
            checked={report.selected}
            onChange={() => reportToggled(report.activityId)}
          />
        </td>,
        <td
          key="cellChildren-td-2"
          className={
            state === 'trashed'
              ? 'reports-name-trash-column'
              : 'reports-name-column'
          }
        >
          {nameChildren}
        </td>,
        <td
          key="cellChildren-td-3"
          className={
            state === 'trashed'
              ? 'reports-date-trash-column'
              : 'reports-date-column'
          }
        >
          {dateChildren}
        </td>,
        <td key="cellChildren-td-4" className="reports-room-column">
          {report.room}
        </td>,
        <td key="cellChildren-td-5" className="reports-type-column">
          {report.displayType}
        </td>,
      ];

      if (state === 'trashed') {
        if (platform.isMobile()) {
          cellChildren.push(
            <td className="reports-more-column">
              <MoreButton
                className="reports-more-button"
                arrowClass="reports-more-menu-arrow"
                menuClass="reports-more-menu"
                menuItems={[
                  <div
                    key="menuItems-div-1"
                    className="reports-more-menu-item"
                    onClick={() => restoreClicked(report.activityId)}
                  >
                    <RestoreIcon className="more-restore-icon" />
                    <span className="more-menu-text">
                      {translate('Restore', true)}
                    </span>
                  </div>,
                  <div
                    key="menuItems-div-2"
                    className="reports-more-menu-item"
                    onClick={() => purgeClicked(report.activityId)}
                  >
                    <TrashIcon className="more-purge-icon" color="#8cb4d2" />
                    <span className="more-menu-text">
                      {translate('Delete Forever', true)}
                    </span>
                  </div>,
                ]}
              />
            </td>
          );
        } else {
          cellChildren.push(
            <td className="reports-restore-column">
              <div
                className="restore-report-icon-button"
                title={translate('Restore Report')}
                onClick={() => restoreClicked(report.activityId)}
              >
                <RestoreIcon className="restore-report-icon" />
              </div>
            </td>,
            <td className="reports-purge-column">
              <div
                className="purge-report-icon-button"
                title={translate('Delete Report Forever')}
                onClick={() => purgeClicked(report.activityId)}
              >
                <TrashIcon className="purge-report-icon" color="#8cb4d2" />
              </div>
            </td>
          );
        }
      }

      tableChildren.push(
        <tr key={`${report.activityId}-tableChildren-tr-2`}>{cellChildren}</tr>
      );
    }

    if (sortColumn === 'date' && sortDirection === 'desc') {
      tableChildren = [
        ...tableChildren,
        <EndOfReports.End key="EndOfReports.End" />,
      ];
    }
    if (hasSearched && searchText.length > 0) {
      const resultsText =
        totalResults === 1
          ? translate('{0} result for “{1}”')
          : translate('{0} results for “{1}”');

      reportsChildren = [
        <span key="reportsChildren-span-1" className="search-results-for-text">
          {resultsText.format(totalResults, searchText)}
        </span>,
      ];
    }

    reportsChildren.push(
      <div key="reportsChildren-div-1" className="state-change-buttons">
        {stateChangeButtons}
      </div>,
      <table
        key="reportsChildren-table-1"
        className={
          'reports-table' +
          (totalResults > Constants.REPORTS_SEARCH_LIMIT
            ? ' reports-table-with-pagination'
            : '')
        }
      >
        <tbody>{tableChildren}</tbody>
      </table>
    );
  }

  let displayState = utils.capitalize(state);

  if (displayState === 'Trashed') {
    displayState = translate('Trash');
  } else if (displayState === 'Deleted_rooms') {
    displayState = translate('From Deleted Rooms');
  }

  if (
    state !== 'deleted_rooms' &&
    state !== 'active' &&
    state !== 'archived' &&
    state !== 'trashed'
  ) {
    displayState = displayState.toUpperCase();
  }

  displayState = utils.ellipsify(displayState, 16);

  const searchContainerChildren = [];

  if (showBanner && bannerText) {
    searchContainerChildren.push(
      <div key="searchContainerChildren-div-1" className="reports-banner">
        <CheckIcon color="#ffffff" svgClass="reports-banner-icon" />
        <span className="reports-banner-text">{bannerText}</span>
        <span className="reports-banner-sub-text">{bannerSubText}</span>
      </div>
    );
  }

  const searchInputPlaceholder = platform.isPhone()
    ? translate('Search')
    : translate('Search {0}').format(displayState);

  searchContainerChildren.push(
    <SearchIcon
      key="searchContainerChildren-SearchIcon-1"
      className="reports-search-icon"
    />,
    <Input
      key="searchContainerChildren-Input-1"
      id="reports-search-input"
      maxLength={256}
      onChange={(event: any) => searchTextChanged(event.target.value)}
      onKeyPress={(event: any) => {
        if (event.key === 'Enter') {
          if (platform.isMobile()) {
            event.target.blur();
          }
          searchClicked();
        }
      }}
      placeholder={searchInputPlaceholder}
      type="text"
      value={searchText || ''}
    />
  );

  if (searchText.length > 0) {
    searchContainerChildren.push(
      <div
        key="searchContainerChildren-div-2"
        className="clear-search"
        onClick={clearSearchClicked}
      >
        <CloseX className="clear-search-x" color={Constants.CLEAR_INPUT_X} />
      </div>
    );
  }

  const roomsReports = [];
  for (const room in model.roomNames) {
    // eslint-disable-next-line no-prototype-builtins
    if (model.roomNames.hasOwnProperty(room)) {
      let title = room;
      if (title === 'deleted_rooms') {
        continue;
      } else {
        title = title.toUpperCase();
        title = utils.ellipsify(title, 20);
      }

      roomsReports.push({
        title: title,
        value: room,
        selected: room === state,
      });
    }
  }

  roomsReports.sort(function (a, b) {
    if (a.title > b.title) {
      return 1;
    }
    if (a.title < b.title) {
      return -1;
    }
    return 0;
  });

  const roomsGroup = {
    children: roomsReports,
    selectable: false,
    title: translate('Rooms'),
  };

  const activeText = translate('Active');
  const archivedText = translate('Archived');
  let deletedRoomsText = translate('From Deleted Rooms ({0})');
  // December 2021 removing count from string until new translation can be ordered
  deletedRoomsText = deletedRoomsText.replace('({0})', '');
  const trashedText = translate('Trash');

  const allGroup = {
    children: [
      { title: activeText, value: 'active', selected: state === 'active' },
      {
        title: archivedText,
        value: 'archived',
        selected: state === 'archived',
      },
      {
        title: deletedRoomsText,
        value: 'deleted_rooms',
        selected: state === 'deleted_rooms',
      },
      {
        title: trashedText,
        value: 'trashed',
        selected: state === 'trashed',
      },
    ],
    selectable: false,
    title: translate('All'),
  };

  const filterOptions = [roomsGroup, allGroup];

  searchContainerChildren.push(
    <button
      key="searchContainerChildren-button-1"
      className="button button-large button-secondary reports-search-button"
      onClick={searchClicked}
    >
      {translate('Search', true)}
    </button>,
    <div
      key="searchContainerChildren-div-3"
      className="reports-filter-container"
    >
      <span className="filter-by-text">{translate('Filter by')}</span>
      <Select
        disabled={fetchingReports}
        onSelect={(_name: any, value: string) => stateChanged(value)}
      >
        {filterOptions}
      </Select>
    </div>
  );

  return (
    <div id="reports-outer-container">
      {' '}
      <div
        key="allChildren-div-1"
        id="reports-inner-container"
        className={
          'form' +
          (totalResults > Constants.REPORTS_SEARCH_LIMIT &&
          !fetchingReports &&
          reports.length > 0 &&
          Math.ceil(totalResults / Constants.REPORTS_SEARCH_LIMIT) > 1
            ? ' reports-with-pages' + ''
            : '')
        }
      >
        <h1 className="text-lg font-700 font-display text-blue-900">
          {translate('Reports')}
        </h1>
        <div className="reports-search-bar">
          <div className="reports-search-container">
            {searchContainerChildren}
          </div>
        </div>
        {reportsChildren}
      </div>
      {totalResults > Constants.REPORTS_SEARCH_LIMIT &&
      !fetchingReports &&
      reports.length > 0 &&
      Math.ceil(totalResults / Constants.REPORTS_SEARCH_LIMIT) > 1 ? (
        <Pagination
          key="allChildren-Pagination-1"
          currentPage={page}
          itemsPerPage={Constants.REPORTS_SEARCH_LIMIT}
          nextClicked={nextClicked}
          pageClicked={pageClicked}
          previousClicked={previousClicked}
          totalItems={totalResults}
        />
      ) : null}
    </div>
  );
}
